import { useEffect, useMemo, useState } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import {
  CHAT_PEOPLE_SEARCH_MAX_LENGTH,
  CHAT_PEOPLE_SEARCH_MIN_LENGTH,
} from '@/constants/chat';
import { chatService } from '@/services/chatService';
import {
  ChatMemberState,
  ConversationType,
  InboxFilter,
  type ChatConversationMember,
  type ChatPerson,
} from '@/types/chat';
import { chatQueryKeys } from './chatQueries';

/** How many of the user's own chats seed the candidate list. */
const SEED_CONVERSATIONS = 8;
const SEED_MEMBERS_PER_CHAT = 100;
const SEARCH_DEBOUNCE_MS = 300;
const SEED_TYPES = new Set<ConversationType>([
  ConversationType.DIRECT,
  ConversationType.GROUP,
  ConversationType.CLAN,
  ConversationType.TEAM,
  ConversationType.ORGANIZATION,
]);

/**
 * Merge the people search results with the people from the user's chats: search hits first, then
 * chat members whose name or username contains `search` (case-insensitive), without excluded ids
 * or duplicates.
 */
export function mergePeople(
  memberLists: ChatConversationMember[][],
  {
    search,
    excludeIds,
    found = [],
  }: {
    search: string;
    excludeIds: ReadonlySet<string>;
    found?: ChatPerson[];
  },
): ChatPerson[] {
  const seen = new Set<string>(excludeIds);
  const people: ChatPerson[] = [];
  const add = (person: ChatPerson) => {
    if (!person.id || seen.has(person.id)) return;
    seen.add(person.id);
    people.push(person);
  };
  found.forEach(add);
  const needle = search.trim().replace(/^@/, '').toLowerCase();
  for (const list of memberLists) {
    for (const member of list) {
      if (member.state !== ChatMemberState.ACTIVE) continue;
      const haystack =
        `${member.name ?? ''} ${member.username ?? ''}`.toLowerCase();
      if (needle && !haystack.includes(needle)) continue;
      add({
        id: member.userId,
        name: member.name,
        username: member.username,
        profilePicture: member.profilePicture ?? null,
      });
    }
  }
  return people;
}

function useDebounced(value: string, ms: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

/**
 * Candidates for a new direct chat or group: the chat people search (username prefix, blocked
 * people left out) once two characters are typed, plus the people in the user's clan, team, group
 * and direct chats, so there is something to pick before typing.
 */
export function usePeoplePicker(
  search: string,
  {
    excludeIds = [],
    enabled = true,
  }: { excludeIds?: string[]; enabled?: boolean } = {},
) {
  const seed = useQuery({
    queryKey: chatQueryKeys.peopleSeed(),
    queryFn: () => chatService.getInbox({ filter: InboxFilter.ALL, limit: 50 }),
    enabled,
    staleTime: 5 * 60_000,
  });
  const seedIds = useMemo(
    () =>
      (seed.data?.items ?? [])
        .filter(item => SEED_TYPES.has(item.type) && !item.request)
        .slice(0, SEED_CONVERSATIONS)
        .map(item => item.conversationId),
    [seed.data],
  );
  const memberQueries = useQueries({
    queries: seedIds.map(conversationId => ({
      queryKey: [...chatQueryKeys.members(conversationId), 'picker'],
      queryFn: () =>
        chatService.getMembers(conversationId, {
          limit: SEED_MEMBERS_PER_CHAT,
        }),
      enabled,
      staleTime: 5 * 60_000,
    })),
  });

  const query = useDebounced(search.trim(), SEARCH_DEBOUNCE_MS)
    .replace(/^@/, '')
    .slice(0, CHAT_PEOPLE_SEARCH_MAX_LENGTH);
  const searchQuery = useQuery({
    queryKey: [...chatQueryKeys.people(), 'search', query.toLowerCase()],
    queryFn: () => chatService.searchUsers(query),
    enabled: enabled && query.length >= CHAT_PEOPLE_SEARCH_MIN_LENGTH,
    staleTime: 60_000,
  });

  const memberKey = memberQueries.map(q => q.dataUpdatedAt).join(',');
  const excludeKey = excludeIds.join(',');
  const found = searchQuery.data;
  const people = useMemo(
    () =>
      mergePeople(
        memberQueries.map(q => q.data?.members ?? []),
        { search, excludeIds: new Set(excludeIds), found },
      ),
    // The query array is rebuilt every render; memberKey and excludeKey track its content.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [memberKey, search, excludeKey, found],
  );

  return {
    people,
    isLoading: seed.isLoading || memberQueries.some(q => q.isLoading),
    isSearching: searchQuery.isFetching,
  };
}
